import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useId,
  useImperativeHandle,
  useRef,
  Ref,
  RefAttributes,
  type JSX,
} from "react";

import { Stack } from "@mui/material";
import { FieldValues, useController, useWatch } from "react-hook-form";

import {
  BaseFormCheckboxGroupProps,
  FormCheckboxGroupProps,
  SubscribedFormCheckboxGroupProps,
  FormCheckboxGroupHandle,
} from "./types";

import { useValidators } from "@/common/hooks/useValidators";
import { FormCheckboxProps } from "@/forms-design-system/FormCheckbox";
import { FormField, getFormFieldProps } from "@/forms-design-system/FormField";

function FormCheckboxGroupInner<T extends FieldValues>(
  { control, rules, name, shouldUnregister, ...rest }: FormCheckboxGroupProps<T>,
  ref: Ref<FormCheckboxGroupHandle>
) {
  return control && name ? (
    <SubscribedFormCheckboxGroup
      {...rest}
      ref={ref}
      control={control}
      rules={rules}
      name={name}
      shouldUnregister={shouldUnregister}
    />
  ) : (
    <BaseFormCheckboxGroup {...rest} name={name} ref={ref} />
  );
}

FormCheckboxGroupInner.displayName = "FormCheckboxGroup";

export const FormCheckboxGroup = forwardRef(FormCheckboxGroupInner) as <T extends FieldValues>(
  props: FormCheckboxGroupProps<T> & RefAttributes<FormCheckboxGroupHandle>
) => JSX.Element;

function SubscribedFormCheckboxGroupInner<T extends FieldValues>(
  {
    control,
    defaultValue,
    name,
    onBlur,
    onChange,
    rules,
    shouldUnregister,
    ...rest
  }: SubscribedFormCheckboxGroupProps<T>,
  ref: Ref<FormCheckboxGroupHandle>
) {
  const validators = useValidators(rules);
  const watchedValue = useWatch({ control, name });
  const internalDefaultValue = defaultValue || watchedValue;

  const { field } = useController<T>({
    name,
    control,
    defaultValue: internalDefaultValue as never,
    rules: validators,
    shouldUnregister,
  });

  const handleChange = useCallback(
    (values: string[]) => {
      onChange?.(values);
      field.onChange(values);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      field.onBlur();
    },
    [field, onBlur]
  );

  return (
    <BaseFormCheckboxGroup
      {...rest}
      value={field.value || []}
      ref={ref}
      onBlur={handleBlur}
      onChange={handleChange}
      data-test={rest?.["data-test"] || "form-checkbox-group"}
      name={name}
    />
  );
}

const SubscribedFormCheckboxGroup = forwardRef(SubscribedFormCheckboxGroupInner) as <T extends FieldValues>(
  props: SubscribedFormCheckboxGroupProps<T> & RefAttributes<FormCheckboxGroupHandle>
) => JSX.Element;

const BaseFormCheckboxGroup = forwardRef<FormCheckboxGroupHandle, BaseFormCheckboxGroupProps>(
  ({ id, children, disabled, error, value, onChange, onBlur, ...rest }, ref) => {
    const firstCheckboxRef = useRef<HTMLInputElement>(null);
    const generatedLabelId = useId();
    const labelId = id ?? generatedLabelId;

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          if (firstCheckboxRef.current) {
            firstCheckboxRef.current.focus();
          }
        },
      }),
      [firstCheckboxRef]
    );

    const handleOnChange = useCallback(
      (checkboxValue: string, checked: boolean) => {
        const newValues = value?.filter((v) => v !== checkboxValue);

        if (checked) {
          newValues.push(checkboxValue);
        }

        onChange?.(newValues);
      },
      [onChange, value]
    );

    return (
      <FormField
        labelId={labelId}
        error={error}
        disabled // Prevent pointer events on label without disabling tooltip
        data-test={rest["data-test"]}
        {...getFormFieldProps(rest)}
      >
        <Stack gap={1} role="group" aria-labelledby={rest.label ? labelId : undefined}>
          {Children.map(children, (child, index) => {
            if (isValidElement(child)) {
              const props = child.props as FormCheckboxProps<FieldValues>;

              return cloneElement<FormCheckboxProps<FieldValues>>(child, {
                ...props,
                inputRef: index === 0 ? firstCheckboxRef : props.inputRef,
                size: props.size ?? "small",
                error: props.error ?? error,
                disabled: props.disabled ?? disabled,
                checked: value.includes(props.value as string),
                onChange: props.onChange ?? ((_e, checked) => handleOnChange(props.value as string, checked)),
                onBlur,
              });
            }

            return null;
          })}
        </Stack>
      </FormField>
    );
  }
);

BaseFormCheckboxGroup.displayName = "BaseFormCheckboxGroup";
